import React, { memo, type ComponentProps, type ReactNode } from 'react';

import { di } from 'react-magnetic-di';

import RovoConversationActions from '@atlassian/rovo-conversation-actions/rovo-conversation-actions';
import { RovoSkillsConfigProvider } from '@atlassian/rovo-skills/services/rovo-skills-config-provider';

import { useRovoSkillsProviderConfig } from '../../../rovo-skills-provider-config-stabilization';
import { ConversationAssistantUIModes } from '../../../types';

type ConversationActionsProps = ComponentProps<typeof RovoConversationActions>;

const RovoConversationActionsWithDi = (props: ConversationActionsProps) => {
	di(RovoConversationActions);

	return <RovoConversationActions {...props} />;
};

const MemoizedRovoConversationActions = memo(RovoConversationActionsWithDi);

type RovoEmptyStateConversationActionsProps = Omit<
	ConversationActionsProps,
	'isInSideBar' | 'onNestedViewChange'
> & {
	isRenderStabilizationEnabled: boolean;
	mode: ConversationAssistantUIModes;
	onNestedViewChange: (isNested: boolean) => void;
	skillRegistryTags?: string[];
};

const RovoConversationActionsSkillsConfigProvider = ({
	children,
	skillRegistryTags,
}: {
	children: ReactNode;
	skillRegistryTags: string[];
}) => {
	const rovoSkillsConfig = useRovoSkillsProviderConfig({
		tags: { skillRegistryTags },
	});

	return <RovoSkillsConfigProvider config={rovoSkillsConfig}>{children}</RovoSkillsConfigProvider>;
};

type StabilizedConversationActionsProps = ConversationActionsProps & {
	skillRegistryTags?: string[];
};

const StabilizedConversationActions = ({
	closeChat,
	sendMessage,
	setChatInputPlaceholderOverride,
	handleClickDynamicPrompt,
	isInSideBar,
	assistanceServiceParams,
	setActiveAction,
	onDiscoverPromptLibraryClick,
	analyticsAttributes,
	onNestedViewChange,
	skillRegistryTags,
}: StabilizedConversationActionsProps) => {
	const conversationActions = (
		<MemoizedRovoConversationActions
			closeChat={closeChat}
			sendMessage={sendMessage}
			setChatInputPlaceholderOverride={setChatInputPlaceholderOverride}
			handleClickDynamicPrompt={handleClickDynamicPrompt}
			isInSideBar={isInSideBar}
			assistanceServiceParams={assistanceServiceParams}
			setActiveAction={setActiveAction}
			onDiscoverPromptLibraryClick={onDiscoverPromptLibraryClick}
			analyticsAttributes={analyticsAttributes}
			onNestedViewChange={onNestedViewChange}
		/>
	);

	if (skillRegistryTags) {
		return (
			<RovoConversationActionsSkillsConfigProvider skillRegistryTags={skillRegistryTags}>
				{conversationActions}
			</RovoConversationActionsSkillsConfigProvider>
		);
	}

	return conversationActions;
};

export const RovoEmptyStateConversationActions = ({
	closeChat,
	sendMessage,
	setChatInputPlaceholderOverride,
	handleClickDynamicPrompt,
	mode,
	assistanceServiceParams,
	setActiveAction,
	onDiscoverPromptLibraryClick,
	analyticsAttributes,
	onNestedViewChange,
	skillRegistryTags,
	isRenderStabilizationEnabled,
}: RovoEmptyStateConversationActionsProps): JSX.Element => {
	di(RovoConversationActions);

	if (isRenderStabilizationEnabled) {
		return (
			<StabilizedConversationActions
				closeChat={closeChat}
				sendMessage={sendMessage}
				setChatInputPlaceholderOverride={setChatInputPlaceholderOverride}
				handleClickDynamicPrompt={handleClickDynamicPrompt}
				isInSideBar={mode === ConversationAssistantUIModes.SIDEBAR}
				assistanceServiceParams={assistanceServiceParams}
				setActiveAction={setActiveAction}
				onDiscoverPromptLibraryClick={onDiscoverPromptLibraryClick}
				analyticsAttributes={analyticsAttributes}
				onNestedViewChange={onNestedViewChange}
				skillRegistryTags={skillRegistryTags}
			/>
		);
	}

	const legacyConversationActionsElement = (
		<RovoConversationActions
			closeChat={closeChat}
			sendMessage={sendMessage}
			setChatInputPlaceholderOverride={setChatInputPlaceholderOverride}
			handleClickDynamicPrompt={handleClickDynamicPrompt}
			isInSideBar={mode === ConversationAssistantUIModes.SIDEBAR}
			assistanceServiceParams={assistanceServiceParams}
			setActiveAction={setActiveAction}
			onDiscoverPromptLibraryClick={onDiscoverPromptLibraryClick}
			analyticsAttributes={analyticsAttributes}
			onNestedViewChange={onNestedViewChange}
		/>
	);

	if (skillRegistryTags) {
		return (
			<RovoConversationActionsSkillsConfigProvider skillRegistryTags={skillRegistryTags}>
				{legacyConversationActionsElement}
			</RovoConversationActionsSkillsConfigProvider>
		);
	}

	return legacyConversationActionsElement;
};
